import { useTranslation } from 'react-i18next';
import type { PAGE_POSITIONS } from 'types';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';

import ChevronRightStretched from '~/assets/icons/ChevronRightStretch';
import AffiliateProductLink from '~/components/AffiliateProductLink';
import DiscountCode from '~/components/DiscountCode';
import {
  type StockStatus as StockStatusType,
  textColours
} from '~/domain/stockStatus';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import { formatReleaseDateLong } from '~/utils/dates';
import { getStockStatusForSize } from '~/utils/listings';

import AffiliateSizes from './AffiliateSizes';
import ShippingAndPrice from './ShippingAndPrice';

type Props = {
  listing: AffiliateStockListing;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
};

export default function StockListing({
  listing,
  selectedSize,
  position,
  pagePosition
}: Props) {
  const product = usePageData<CapiProductPage>();
  const { t } = useTranslation('pdp');

  const stockStatus = getStockStatusForSize(listing, selectedSize);
  const isMarketplace = listing.affiliate.id === 'affiliate:marketplace';

  return (
    <AffiliateProductLink
      price={listing.price}
      affiliate={listing.affiliate}
      product={product}
      buttonPosition={position}
      pagePosition={pagePosition}
      urlTemplates={listing}
      selectedSize={selectedSize}
      variant="none"
      className="flex size-full flex-col justify-center gap-4 rounded-lg bg-white p-4"
    >
      <div className="flex w-full items-center gap-4 bg-white">
        <img
          src={listing.affiliate.logo}
          alt={listing.affiliate.name}
          className="h-9 w-16 bg-white object-cover"
        />
        <StockStatus
          stockStatus={stockStatus}
          listing={listing}
          selectedSize={selectedSize}
        />
        <div className="flex grow justify-end gap-4">
          <ShippingAndPrice listing={listing} selectedSize={selectedSize} />
          {stockStatus !== 'in-stock' && (
            <div className="flex self-center justify-self-end">
              <ChevronRightStretched
                height="32"
                width="12"
                stroke="#F2F2F2"
                strokeWidth="2"
              />
            </div>
          )}
        </div>
      </div>
      <AffiliateSizes selectedSize={selectedSize} sizes={listing.sizes} />
      {listing.discount_code?.code && (
        <DiscountCode
          code={listing.discount_code?.code}
          amount={listing.discount_code?.amount}
        />
      )}
      {stockStatus === 'in-stock' && (
        <div className="w-full rounded-lg border border-forest bg-forest px-4 py-2 text-center font-semibold text-white transition duration-200 ease-in-out hover:border-green-700 hover:bg-green-700 active:scale-95 active:border-green-700 active:bg-green-700">
          {isMarketplace ? (
            'Buy from our marketplace'
          ) : (
            <span>{t('buyFrom', { brand: listing.affiliate.name })}</span>
          )}
        </div>
      )}
    </AffiliateProductLink>
  );
}

export function StockStatus({
  stockStatus,
  listing,
  selectedSize
}: {
  listing: AffiliateStockListing;
  selectedSize?: string;
  stockStatus: StockStatusType;
}) {
  const { t } = useTranslation();
  const region = useRegion();

  switch (stockStatus) {
    case 'in-stock':
      return (
        <div className="flex flex-col justify-center">
          {listing.affiliate.is_reseller ? (
            <>
              <p className={`text-sm ${textColours['reseller']} md:text-base`}>
                Marketplace
              </p>
              {listing.affiliate.id === 'affiliate:marketplace' && (
                <p className="text-xs text-black md:text-sm">
                  Verified sellers
                </p>
              )}
            </>
          ) : (
            <p className={`text-sm ${textColours[stockStatus]} md:text-base`}>
              {t(stockStatus)}
            </p>
          )}
        </div>
      );
    case 'coming-soon':
      return (
        <div className="flex flex-col text-sm md:text-base">
          <p className={`${textColours[stockStatus]}`}>{t(stockStatus)}</p>
          <span className="text-sm text-grey-read">
            {listing.release_date
              ? formatReleaseDateLong(listing.release_date, region)
              : t('tbcLabel')}
          </span>
        </div>
      );
    case 'sold-out':
      return (
        <div className={`text-sm ${textColours[stockStatus]} md:text-base`}>
          {listing.sizes.length && selectedSize ? (
            <>
              <p className="text-nowrap">{t(stockStatus)}</p>
              <p className="text-black">UK {selectedSize}</p>
            </>
          ) : (
            t(stockStatus)
          )}
        </div>
      );
    case 'tbc':
    default:
      return <p className={textColours[stockStatus]}>{t('tbc')}</p>;
  }
}
